import type { Activity, CreatorView, MeetingView } from '@sorted/types'
import { useState } from 'react'
import { ACTIVITY_ICON, Send } from '../components/icons.js'
import { Head, Screen } from '../components/Screen.js'
import { api } from '../lib/api.js'
import { activityLabel, meetingWhenLong, whatsappHref } from '../lib/format.js'
import { t } from '../lib/i18n.js'

/**
 * Alice, waiting - spec §2, invariants 1 and 3.
 *
 * Bob holds the ball, so this screen has nothing for her to decide. It shows
 * where things stand and offers exactly one exit: the terminal opt-out, which
 * hands off to WhatsApp rather than starting a negotiation (invariant 2 - she
 * has no veto, only a way out).
 */

interface Props {
  meeting: CreatorView
  token: string
  onUpdate: (v: MeetingView) => void
}

export function Waiting({ meeting, token }: Props) {
  const [confirming, setConfirming] = useState(false)
  const [handoff, setHandoff] = useState<string | null>(null)
  const activity = meeting.activity as Activity
  const Icon = ACTIVITY_ICON[activity]

  const shareUrl = meeting.shareUrl || `${window.location.origin}/${token}`
  const invite = [
    t('share.invite', {
      activity: activityLabel(activity),
      when: meetingWhenLong(meeting.meetAt),
    }),
    shareUrl,
  ].join('\n')

  if (handoff) {
    return (
      <Screen>
        <Head title={t('wait.handoffTitle')} lede={t('wait.handoffBody')} />
        <a
          className="btn btn-primary mt"
          href={whatsappHref(handoff)}
          target="_blank"
          rel="noreferrer"
        >
          <Send size={18} aria-hidden />
          {t('wait.openWhatsapp')}
        </a>
      </Screen>
    )
  }

  async function optOut() {
    try {
      setHandoff((await api.optOut(token)).handoffText)
    } catch {
      setConfirming(false)
    }
  }

  /* The only exit. Deliberately quiet - a last resort, not a veto, and it must
     not read as "reject their choice". */
  const exit = confirming ? (
    <div className="stack">
      <p className="faint center">{t('wait.optOutWarn')}</p>
      <div className="btn-row">
        <button className="btn" onClick={() => setConfirming(false)}>
          {t('wait.keepWaiting')}
        </button>
        <button className="btn" onClick={() => void optOut()}>
          {t('wait.sortDirectly')}
        </button>
      </div>
    </div>
  ) : (
    <button className="btn btn-ghost" onClick={() => setConfirming(true)}>
      {t('wait.optOut')}
    </button>
  )

  return (
    <Screen>
      <p className="when-line title-with-icon">
        <Icon size={19} strokeWidth={1.5} aria-hidden />
        {meetingWhenLong(meeting.meetAt)}
      </p>

      <Head
        title={t(meeting.opened ? 'wait.picking' : 'wait.unopened')}
        lede={t(meeting.opened ? 'wait.pickingHint' : 'wait.unopenedHint')}
      />

      {!meeting.opened && (
        <>
          <div className="card mt">
            <p className="faint link-row">{shareUrl}</p>
          </div>
          <p className="faint mt-sm">{t('share.ownLinkHint')}</p>

          <a
            className="btn btn-primary mt"
            href={whatsappHref(invite)}
            target="_blank"
            rel="noreferrer"
          >
            <Send size={18} aria-hidden />
            {t('share.whatsapp')}
          </a>
        </>
      )}

      {/* In flow, right after the content - not pushed to the bottom of the
          viewport. A spacer that grows leaves a lake of whitespace on a tall
          screen and puts the one control below the fold on a short one. */}
      <div className="mt">{exit}</div>
    </Screen>
  )
}
